import { Box, styled } from '@mui/material';
import { useCallback } from 'react';

import {
  ActiveCaseCard,
  CASE_STATUS_INDEX,
  ENOPCaseStatus,
  useDetectUpdatedCaseFields,
  type ActiveCase,
} from '#entities';
import { ELayoutManagerMode, useCardLayout, useNewItemDetector } from '#shared';

import { Carousel, CarouselStatic } from '../../Carousel';
import { CaseDetailsDrawer, useCaseSelection } from '../../CaseDrawer';
import { useAppBehavior, useData } from '../../data-context';
import { useLayoutMode } from '../../LayoutManager';
import { useWidgetItemsDistribution } from '../../WidgetDistribution';

import type { WidgetProps } from '../../widgetManager';

const activeCaseFieldsToCompare: (keyof ActiveCase)[] = [
  'label',
  'donorType',
  'status',
  'organ',
  'pathLabels',
  'transport',
  'trips',
];

const PREFIX = `TSMED-ActiveCaseCards`;
const activeCaseCardsClasses = {
  root: `${PREFIX}-root`,
};
const Root = styled(Box)(() => ({
  [`&.${activeCaseCardsClasses.root}`]: {
    height: '100%',
    width: '100%',
    display: 'flex',
  },
}));

export interface ActiveCaseCardsProps {
  instance: WidgetProps['instance'];
  caseStatuses?: ENOPCaseStatus[];
  sortBy?:
    | keyof Pick<
        ActiveCase,
        'id' | 'donorType' | 'label' | 'status' | 'donorORTimeString'
      >
    | 'oscStatusSequence';
  maxRows?: number;
  highlightDurationSeconds?: number;
  maxHeight?: number;
  availableHeight?: number;
  showReturningToBase?: boolean;
  shareSettings?: boolean;
  variant?: 'horizontal' | 'vertical';
  instanceOptions?: {
    totalInstances?: number;
    instanceIndex?: number;
  };
}

const CARD_MIN_WIDTH = 140;
const CARD_MAX_WIDTH = 300;
const CARD_MIN_HEIGHT = 160;
const CARD_MAX_HEIGHT = 200;
const GAP = 8;

/**
 * ActiveCaseCard component for displaying active case alerts.
 * Follows the project's card design patterns and performance guidelines.
 */
export const ActiveCaseCards = ({
  instance,
  variant = 'horizontal',
  caseStatuses = [],
  sortBy = 'donorORTimeString',
  maxRows = 0,
  highlightDurationSeconds = 10,
  maxHeight = 0,
  showReturningToBase = false,
  instanceOptions,
}: ActiveCaseCardsProps) => {
  const { filteredActiveCases: activeCases } = useData();
  const { onCaseLoopNext } = useAppBehavior();
  const { mode } = useLayoutMode();
  const isPreviewMode = mode !== ELayoutManagerMode.RENDER;
  const { updatedCases } = useDetectUpdatedCaseFields(activeCases, {
    exactFieldsOnly: activeCaseFieldsToCompare,
  });
  const { isNew } = useNewItemDetector(activeCases, item => item.id);
  const {
    open: isCaseDrawerOpen,
    selectedCase,
    handleClose: handleCaseDrawerClose,
    selectedCaseType,
    handleSelectCaseById,
  } = useCaseSelection();

  let list: ActiveCase[] = [];
  if (caseStatuses.length > 0) {
    list = activeCases.filter(item => {
      const isStatusMatch = caseStatuses.includes(item.status);
      const showReturning =
        showReturningToBase || item.status !== ENOPCaseStatus.ReturningToBase;
      return isStatusMatch && showReturning;
    });
  } else {
    list = showReturningToBase
      ? activeCases
      : activeCases.filter(
          item => item.status !== ENOPCaseStatus.ReturningToBase,
        );
  }
  if (sortBy) {
    if (sortBy === 'oscStatusSequence') {
      list = [...list].sort(
        (a, b) => CASE_STATUS_INDEX[a.status] - CASE_STATUS_INDEX[b.status],
      );
    } else {
      list = [...list].sort((a, b) => (a[sortBy] < b[sortBy] ? -1 : 1));
    }
  }

  const items: Array<ActiveCase & { updatedFields: string[] }> = list.map(
    c => ({
      ...c,
      updatedFields: updatedCases[c.id] ?? [],
      isNewOnBoard: isNew(c.id),
    }),
  );

  const availableRowsCount = Math.floor(
    (maxHeight + GAP) / (CARD_MIN_HEIGHT + GAP),
  );

  const { items: distributed } = useWidgetItemsDistribution(
    items,
    instance,
    variant,
    undefined,
    {
      totalInstances: instanceOptions?.totalInstances,
      instanceIndex: instanceOptions?.instanceIndex,
    },
  );

  const { itemWidth, itemHeight, rowsCount, handleResize, isCoverFullHeight } =
    useCardLayout({
      itemsCount: distributed?.length ?? 0,
      maxRows,
      availableHeight: maxHeight,
      maxCardWidth: CARD_MAX_WIDTH,
      minCardWidth: CARD_MIN_WIDTH,
      minCardHeight: CARD_MIN_HEIGHT,
      maxCardHeight: CARD_MAX_HEIGHT,
      gap: GAP,
    });

  const itemsToRender = distributed;

  const handleCardClick = useCallback(
    (id: string) => handleSelectCaseById(id),
    [handleSelectCaseById],
  );

  const itemProps = {
    highlightDurationMilliseconds: highlightDurationSeconds * 1_000,
    width: itemWidth,
    onClick: handleCardClick,
  };

  const CarouselComponent = isPreviewMode ? CarouselStatic : Carousel;

  return availableRowsCount > 0 ? (
    <Root className={activeCaseCardsClasses.root}>
      <CarouselComponent
        renderItem={activeCase => (
          <ActiveCaseCard activeCase={activeCase} {...itemProps} />
        )}
        items={itemsToRender}
        itemKey={'id'}
        normalSize={rowsCount}
        gap={GAP}
        itemWidth={itemWidth}
        itemHeight={isCoverFullHeight ? itemHeight : CARD_MIN_HEIGHT}
        onResize={handleResize}
        scrollStepSize={isPreviewMode ? undefined : 1}
        isFullHeight={isCoverFullHeight}
        onLoopNext={isPreviewMode ? undefined : onCaseLoopNext}
        variant="horizontal"
        normalStretch
      />

      <CaseDetailsDrawer
        open={isCaseDrawerOpen}
        onClose={handleCaseDrawerClose}
        caseData={selectedCase}
        selectedCaseType={selectedCaseType}
      />
    </Root>
  ) : null;
};

ActiveCaseCards.displayName = 'ActiveCaseCards';
